import React, { useMemo, useState } from 'react';
import NereusCaution from './NereusCaution';
import { useNavigate } from 'react-router-dom';
import {
  Avatar, AvatarGroup, Badge, IconButton, Popover, Drawer, Box, Typography, List, ListItem,
  ListItemText, ListItemIcon, Divider, Button, Chip, useMediaQuery, useTheme,
  Tooltip,
} from '@mui/material';
import {
  Notifications as BellIcon,
  PersonAdd, CheckCircle, Waves, Build, Close, DoneAll, ScubaDiving, LibraryAddCheck,
  Sell, Comment, Favorite, GroupsRounded, Diversity3 as ClubIcon, AdminPanelSettings, PersonAddAlt,
  SmartToy, History, MailOutline, Flag,
} from '@mui/icons-material';
import { AppNotification, NotificationType } from '../services/notificationService';
import { NotificationGroup, groupNotifications } from '../utils/notificationGroups';
import { formatDistanceToNow } from 'date-fns';

function initials(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

interface Props {
  notifications: AppNotification[];
  unreadCount: number;
  onMarkRead: (id: string) => void;
  onMarkAllRead: () => void;
  onDismiss: (id: string) => void;
}

function typeIcon(type: NotificationType) {
  switch (type) {
    case 'BuddyRequest':  return <PersonAdd fontSize="small" color="primary" />;
    case 'BuddyAccepted': return <CheckCircle fontSize="small" color="success" />;
    case 'BuddyActivity': return <Waves fontSize="small" color="info" />;
    case 'EquipmentService': return <Build fontSize="small" color="warning" />;
    case 'DiveShared': return <ScubaDiving fontSize="small" color="primary" />;
    case 'DiveShareAccepted': return <LibraryAddCheck fontSize="small" color="success" />;
    case 'ReportFiled':
    case 'ModerationNotice': return <Flag fontSize="small" color="warning" />;
    case 'InviteAccepted':
    case 'NewFollower': return <PersonAddAlt fontSize="small" color="primary" />;
    case 'PostTagged':
    case 'CommentTagged': return <Sell fontSize="small" color="primary" />;
    case 'PostCommented': return <Comment fontSize="small" color="info" />;
    case 'PostReacted': return <Favorite fontSize="small" color="error" />;
    case 'PostShared': return <ScubaDiving fontSize="small" color="primary" />;
    case 'NewMessage': return <MailOutline fontSize="small" color="primary" />;
    case 'GroupInvite':
    case 'GroupJoinRequest':
    case 'GroupJoinApproved': return <GroupsRounded fontSize="small" color="secondary" />;
    case 'GroupRoleChanged': return <AdminPanelSettings fontSize="small" color="secondary" />;
    case 'GroupTagged': return <Sell fontSize="small" color="secondary" />;
    case 'ClubInvite':
    case 'ClubJoinRequest':
    case 'ClubJoinApproved': return <ClubIcon fontSize="small" color="primary" />;
    case 'ClubRoleChanged': return <AdminPanelSettings fontSize="small" color="primary" />;
    case 'ClubTagged': return <Sell fontSize="small" color="primary" />;
    case 'NereusReplied': return <SmartToy fontSize="small" color="primary" />;
    case 'NereusReplyUnavailable': return <SmartToy fontSize="small" color="disabled" />;
    case 'OpenDiveJoined': return <ScubaDiving fontSize="small" color="primary" />;
    case 'DiveAnniversary': return <History fontSize="small" color="info" />;
    // Unknown types can arrive from a newer backend; render a neutral icon rather than nothing
    default: return <Waves fontSize="small" color="disabled" />;
  }
}

function timeAgo(iso: string) {
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return ''; }
}

// relatedEntityId's meaning depends on the notification type: a postId, groupId, clubId, etc.
function notificationTarget(n: AppNotification): string | null {
  if (!n.relatedEntityId) return null;
  switch (n.type) {
    case 'PostTagged':
    case 'PostCommented':
    case 'PostReacted':
    case 'CommentTagged':
    case 'PostShared':
    case 'NereusReplied':
      return `/post/${n.relatedEntityId}`;
    case 'GroupInvite':
    case 'GroupJoinRequest':
    case 'GroupJoinApproved':
    case 'GroupRoleChanged':
    case 'GroupTagged':
      return `/groups/${n.relatedEntityId}`;
    case 'ClubInvite':
    case 'ClubJoinRequest':
    case 'ClubJoinApproved':
    case 'ClubRoleChanged':
    case 'ClubTagged':
      return `/clubs/${n.relatedEntityId}`;
    case 'BuddyRequest':
    case 'BuddyAccepted':
    case 'BuddyActivity':
      return '/buddies';
    case 'DiveShared':
    case 'DiveShareAccepted':
    case 'OpenDiveJoined':
    case 'DiveAnniversary':
      return `/dive/${n.relatedEntityId}`;
    case 'ReportFiled':
      return '/admin';
    case 'InviteAccepted':
    case 'NewFollower':
      return `/u/${n.relatedEntityId}`;
    case 'NewMessage':
      return `/messages?with=${n.relatedEntityId}`;
    default:
      return null;
  }
}

const NotificationList: React.FC<{
  notifications: AppNotification[];
  onMarkRead: (id: string) => void;
  onMarkAllRead: () => void;
  onDismiss: (id: string) => void;
  onClose: () => void;
}> = ({ notifications, onMarkRead, onMarkAllRead, onDismiss, onClose }) => {
  const navigate = useNavigate();
  const hasUnread = notifications.some(n => !n.read);
  // "Sarah reacted", "Tom reacted", "Priya reacted" on one post become a single row.
  const groups = useMemo(() => groupNotifications(notifications), [notifications]);

  const handleGroupClick = (g: NotificationGroup) => {
    g.items.filter(n => !n.read).forEach(n => onMarkRead(n.notificationId));
    const target = notificationTarget(g.newest);
    if (target) {
      onClose();
      navigate(target);
    }
  };

  const dismissGroup = (g: NotificationGroup) => g.items.forEach(n => onDismiss(n.notificationId));

  return (
    <Box sx={{ width: '100%', maxWidth: 400 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
          Notifications
          {hasUnread && (
            <Chip size="small" label="New" color="primary" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
          )}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {hasUnread && (
            <Tooltip title="Mark all read">
              <IconButton size="small" onClick={onMarkAllRead}>
                <DoneAll fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton size="small" onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      <Divider />

      {groups.length === 0 ? (
        <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
          <BellIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            No notifications yet
          </Typography>
        </Box>
      ) : (
        <List disablePadding sx={{ maxHeight: { xs: '55vh', sm: 360 }, overflowY: 'auto' }}>
          {groups.map((g, idx) => {
            const n = g.newest;
            const clickable = !!notificationTarget(n) || g.unread;
            return (
              <React.Fragment key={g.key}>
                {idx > 0 && <Divider component="li" />}
                <ListItem
                  alignItems="flex-start"
                  onClick={() => handleGroupClick(g)}
                  aria-label={`${g.headline}${g.unread ? ', unread' : ''}`}
                  sx={{
                    cursor: clickable ? 'pointer' : 'default',
                    bgcolor: g.unread ? 'action.hover' : 'transparent',
                    borderLeft: '3px solid',
                    borderLeftColor: g.unread ? 'primary.main' : 'transparent',
                    '&:hover': { bgcolor: 'action.selected' },
                    pr: 1,
                    minHeight: 60,
                  }}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      size="small"
                      aria-label={g.items.length > 1 ? `Dismiss ${g.items.length} notifications` : 'Dismiss notification'}
                      onClick={(e) => { e.stopPropagation(); dismissGroup(g); }}
                      sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemIcon sx={{ minWidth: g.actors.length > 0 ? 56 : 36, mt: 0.5, alignItems: 'center' }}>
                    {g.actors.length > 0 ? (
                      <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={<Box sx={{ display: 'flex', bgcolor: 'background.paper', borderRadius: '50%', p: 0.25, '& svg': { fontSize: 14 } }}>{typeIcon(n.type)}</Box>}
                      >
                        <AvatarGroup
                          max={3}
                          spacing={14}
                          sx={{ '& .MuiAvatar-root': { width: 30, height: 30, fontSize: 12, borderWidth: 2 } }}
                        >
                          {g.actors.map(a => (
                            <Avatar key={a.userId} src={a.profilePhotoUrl ?? undefined} alt={a.displayName} sx={{ bgcolor: 'primary.main' }}>
                              {!a.profilePhotoUrl && (initials(a.displayName) || '?')}
                            </Avatar>
                          ))}
                        </AvatarGroup>
                      </Badge>
                    ) : (
                      typeIcon(n.type)
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography variant="body2" sx={{ fontWeight: g.unread ? 700 : 400, flexGrow: 1 }}>
                          {g.headline}
                        </Typography>
                        {g.unread && (
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }} />
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                        {g.detail && <>{g.detail}<br /></>}
                        {timeAgo(n.createdAt)}
                        {n.type === 'NereusReplied' && <NereusCaution variant="inline" />}
                      </Typography>
                    }
                  />
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>
      )}

      {groups.length > 0 && (
        <>
          <Divider />
          <Box sx={{ px: 2, py: 1, textAlign: 'center' }}>
            <Button size="small" color="primary" onClick={onMarkAllRead} disabled={!hasUnread}>
              Mark all as read
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

const NotificationBell: React.FC<Props> = ({ notifications, unreadCount, onMarkRead, onMarkAllRead, onDismiss }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isMobile) setDrawerOpen(true);
    else setAnchorEl(e.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setDrawerOpen(false);
  };

  const listProps = { notifications, onMarkRead, onMarkAllRead, onDismiss, onClose: handleClose };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          onClick={handleOpen}
          size="large"
          sx={{ color: 'inherit', minHeight: 44, minWidth: 44 }}
          aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        >
          <Badge badgeContent={unreadCount > 0 ? unreadCount : undefined} color="error" max={99}>
            <BellIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      {/* Desktop: Popover */}
      {!isMobile && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { width: 380, mt: 0.5, borderRadius: 2 } }}
        >
          <NotificationList {...listProps} />
        </Popover>
      )}

      {/* Mobile: Bottom sheet Drawer */}
      {isMobile && (
        <Drawer
          anchor="bottom"
          open={drawerOpen}
          onClose={handleClose}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxHeight: '65vh',
            },
          }}
        >
          {/* Handle bar */}
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1, pb: 0.5 }}>
            <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: 'divider' }} />
          </Box>
          <NotificationList {...listProps} />
        </Drawer>
      )}
    </>
  );
};

export default NotificationBell;
